import { isEmpty } from 'lodash'
import AccountApi from '@/api/account'
import { isValidSlideLimits } from '@/utils/slideLimitRuleset'
import { isPlanBenefitScopedToPresentation } from '@/vuex/planBenefitScope'
import { DISTINCT_TYPE_LIMIT_FLAG, parseDistinctTypeLimitVariant, normalizeDistinctTypeLimit } from '@/constant/distinctTypeLimitExperiment.constant'

const SET_PLAN_MUTATION = 'setPlan'

const _state = {
  salesAssistedContract: null,
}

const actions = {
  getSalesAssistedContractData: async ({ commit, rootState }) => {
    const { plan, currentUser } = rootState
    const isFreePlan = plan?.group === 'free'
    const isInTeam = currentUser?.accountId
    // check if user is free plan or not in team
    if (isFreePlan || !isInTeam) return
    const salesAssistedContract = await AccountApi.getSalesAssistedContract()
    commit('setSalesAssistedContract', salesAssistedContract)
  },
  handleUpgradeButtonClick: async ({ dispatch }, payload) => {
    await dispatch('upgradePlanAction', payload, { root: true })
  },
}

const FEATURE_TOGGLES_IS_ENABLED = 'featureToggles/isFeatureEnabled'
const MAXED_OUT_PLAN_GROUPS = ['pro', 'white-label']
const MAXED_OUT_PLAN_TYPES = [
  'pro-monthly',
  'pro-yearly',
  'white-label',
  'edu-large',
  'edu-large-yearly',
  'edu-large-monthly',
]
const UPGRADE_ALLOWED_TEAM_ROLES = ['owner', 'admin']

const isTeamAccountWithoutUpgradePermission = (currentUser) => {
  return currentUser.accountId && !UPGRADE_ALLOWED_TEAM_ROLES.includes(currentUser.accountRole)
}

const isMaxedOutProfileMenuUpgradePlan = (plan, planType) => {
  const planName = (plan.name || '').toLowerCase()
  return MAXED_OUT_PLAN_GROUPS.includes(plan.group) ||
    MAXED_OUT_PLAN_TYPES.includes(plan.type) ||
    MAXED_OUT_PLAN_TYPES.includes(planType) ||
    planName === 'enterprise' ||
    planName.includes('pro') ||
    (planName.includes('edu') && planName.includes('large'))
}

const _getters = {
  canUseNewBadgeComponent: (_, __, ___, rootGetters) => {
    return rootGetters[FEATURE_TOGGLES_IS_ENABLED]('release-new-plan-badge-component')
  },
  isEnterprise: (state, getters) => {
    if (!getters.canUseNewBadgeComponent) return false
    // Enterprise now requires the deal to be explicitly flagged "Is Enterprise = Yes"
    // (the `is_enterprise` column on the HubSpot deal via fct_sales_assisted_contracts).
    // NB: Redshift folds result-column names to lowercase, so the snake_case alias
    // survives readable as `is_enterprise` on the wire (a camelCase alias would arrive
    // as the unreadable `isenterprise`, so snake is kept deliberately).
    // AHA-38266: previously ANY active sales-assisted contract counted, so small owned
    // deals wrongly got the Enterprise experience (Account Manager card, badge, hidden
    // billing). The `status='active'` (not-expired) and team-mapped conditions are still
    // enforced upstream — the contract only reaches here when both already hold.
    return state.salesAssistedContract?.is_enterprise === true
  },
  canUseOneTime2025: (_, __, ___, rootGetters) => {
    return rootGetters[FEATURE_TOGGLES_IS_ENABLED]('release-one-time-2025')
  },
  shouldIgnoreSlideLimits: (_, __, ___, rootGetters) => {
    return Number.isFinite(rootGetters.monthlyParticipantLimit)
  },
  distinctTypeLimitOverride: (_, getters, rootState, rootGetters) => {
    if (getters.shouldIgnoreSlideLimits) return null
    const { plan, planBenefit } = rootState
    const isFreePlan = planBenefit?.name && isPlanBenefitScopedToPresentation()
      ? planBenefit.name === 'free'
      : !plan || plan.group === 'free'
    if (!isFreePlan) return null
    const fromFlag = parseDistinctTypeLimitVariant(rootGetters['featureToggles/getVariant'](DISTINCT_TYPE_LIMIT_FLAG))
    if (fromFlag) return fromFlag
    // BOO-1279: the pricing.experiments.free arm serves the limit inside the Free slideLimits
    // ruleset; the Present wall and picker only engage when this getter returns it.
    return normalizeDistinctTypeLimit(planBenefit?.slideLimits?.distinctTypeLimit)
  },
  canUseSlideLimitsRuleset: (_, getters, rootState) => {
    if (getters.shouldIgnoreSlideLimits) return true
    if (getters.distinctTypeLimitOverride) return true
    const slideLimits = rootState.planBenefit && rootState.planBenefit.slideLimits
    return isValidSlideLimits(slideLimits)
  },
  canShowProfileMenuUpgrade: (state, getters, rootState) => {
    const plan = rootState.plan || {}
    const currentUser = rootState.currentUser || {}
    const planType = currentUser.plan || plan.type || ''

    if (!planType && !plan.name) return false
    if (getters.isEnterprise || !isEmpty(state.salesAssistedContract)) return false
    if (isTeamAccountWithoutUpgradePermission(currentUser)) return false
    if (isMaxedOutProfileMenuUpgradePlan(plan, planType)) return false

    return true
  },
}

const mutations = {
  setSalesAssistedContract(state, payload) {
    state.salesAssistedContract = payload
  },
}

const plugin = (store) => {
  store.subscribe((mutation) => {
    // WHY SET_PLAN?
    // Because the setPlan is always called after setCurrentUser mutation
    // the flow is currentUser -> plan -> salesAssistedContract data
    if (mutation.type !== SET_PLAN_MUTATION) return
    store.dispatch('planModule/getSalesAssistedContractData')
  })
}

export default {
  namespaced: true,
  state: _state,
  actions,
  mutations,
  getters: _getters,
  plugin,
}
